import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Check, Info, RefreshCw, Search, Tag } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { usePermission } from '../../auth/usePermission';
import { PermissionName } from '../../auth/permissions';

/**
 * Admin — Brands.
 *
 * WHAT ACTIVATING A BRAND DOES, said plainly because the word invites the wrong reading: it
 * RANKS, it does not hide. TecDoc lists 1,226 part manufacturers and a single OE number fans out
 * to well over a hundred of them; every one is a candidate the comparator could quote, and each
 * candidate costs a supplier round-trip, so the candidate list is capped. Active brands take the
 * front of that list and get the budget. An inactive brand still appears if a supplier quotes it
 * anyway — a counter operator asked for a part we can source must never be told it doesn't exist
 * because nobody ticked a box here.
 *
 * WHY THIS SCREEN EXISTS AT ALL. The cap used to choose by whatever order the database happened
 * to emit rows in, which made the same brands invisible on every search — VALEO, BLUE PRINT and
 * others were unreachable on references they genuinely fit. That is fixed in the query (the cap
 * is now spread fairly across brands); this screen is what tells the fair cap which brands
 * matter. See migration 102.
 *
 * CARRIED vs ACTIVE. Carried is a fact the sync recomputes — this brand is on our products or in
 * a supplier catalogue. Active is a decision somebody made. The sync seeds active from carried
 * the first time it sees a brand and never touches it again, so re-syncing cannot quietly undo
 * an afternoon of curation.
 */

interface Brand {
  id: string;
  name: string;
  tecdoc_sup_id: number | null;
  logo_file: string | null;
  active: boolean;
  carried: boolean;
}

type Filter = 'ALL' | 'CARRIED' | 'ACTIVE' | 'INACTIVE';

const cmd = () => ({ headers: { 'X-Command-Id': crypto.randomUUID() } });

const FILTER_LABEL_KEY: Record<Filter, string> = {
  CARRIED: 'adminScreens.brands.filter.carried',
  ACTIVE: 'adminScreens.brands.filter.active',
  INACTIVE: 'adminScreens.brands.filter.inactive',
  ALL: 'adminScreens.brands.filter.all',
};

export default function Brands() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canWrite = usePermission(PermissionName.CATALOG_WRITE);

  const [brands, setBrands] = useState<Brand[] | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('CARRIED');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (filter !== 'ALL') params.set('only', filter);
      const { data } = await apiClient.get<{ data: Brand[] }>(`/brands?${params.toString()}`);
      setBrands(data.data);
      setSelected(new Set());
    } catch (err) {
      setBrands([]);
      showApiError(err);
    }
  }, [q, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced: the search hits a LIKE over 1,226 rows and the box is typed into at counter speed.
  useEffect(() => {
    const h = setTimeout(() => void load(), 200);
    return () => clearTimeout(h);
  }, [load]);

  const counts = useMemo(() => {
    const all = brands ?? [];
    return {
      shown: all.length,
      active: all.filter((b) => b.active).length,
      carried: all.filter((b) => b.carried).length,
    };
  }, [brands]);

  const setActive = async (ids: string[], active: boolean) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const { data } = await apiClient.patch<{ data: { changed: number } }>(
        '/brands/active',
        { ids, active },
        cmd()
      );
      push({
        tone: 'success',
        message: active
          ? t('adminScreens.brands.activated', { count: data.data.changed })
          : t('adminScreens.brands.deactivated', { count: data.data.changed }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{
        data: { total: number; inserted: number; carried: number };
      }>('/brands/sync', {}, cmd());
      push({
        tone: 'success',
        message: t('adminScreens.brands.synced', {
          count: data.data.total,
          inserted: data.data.inserted,
          carried: data.data.carried,
        }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleOne = (b: Brand) => {
    if (!canWrite) return;
    void setActive([b.id], !b.active);
  };

  const allShownSelected = (brands ?? []).length > 0 && selected.size === (brands ?? []).length;

  return (
    <div>
      <PageHeader
        title={t('adminScreens.brands.title')}
        description={t('adminScreens.brands.description')}
        actions={
          canWrite && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void sync()}>
              <RefreshCw className="h-4 w-4" /> {t('adminScreens.brands.sync')}
            </Button>
          )
        }
      />

      <div className="mb-4 flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-xs">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1">
          <p>
            <Trans i18nKey="adminScreens.brands.infoActive" components={{ b: <b /> }} />
          </p>
          <p className="text-muted-foreground">
            <Trans i18nKey="adminScreens.brands.infoCarried" components={{ b: <b /> }} />
          </p>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('adminScreens.brands.searchPlaceholder')}
            className="pl-8"
          />
        </div>
        <div className="flex overflow-hidden rounded-md border">
          {(['CARRIED', 'ACTIVE', 'INACTIVE', 'ALL'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs ${filter === f ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            >
              {t(FILTER_LABEL_KEY[f])}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          {t('adminScreens.brands.counts', {
            shown: counts.shown,
            active: counts.active,
            carried: counts.carried,
          })}
        </span>

        {canWrite && selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('adminScreens.brands.selected', { count: selected.size })}
            </span>
            <Button size="sm" disabled={busy} onClick={() => void setActive([...selected], true)}>
              {t('adminScreens.brands.activate')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void setActive([...selected], false)}
            >
              {t('adminScreens.brands.deactivate')}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="relative w-full overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {canWrite && (
                  <th className="h-10 w-10 px-3 text-left align-middle">
                    <input
                      type="checkbox"
                      checked={allShownSelected}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? new Set((brands ?? []).map((b) => b.id)) : new Set()
                        )
                      }
                    />
                  </th>
                )}
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  {t('adminScreens.brands.columns.brand')}
                </th>
                {/* The id the previous system prints beside every brand — the only stable key
                    across TecDoc releases, and what someone compares the two systems on. */}
                <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground">
                  {t('adminScreens.brands.columns.tecdocId')}
                </th>
                <th className="h-10 px-3 text-center align-middle font-medium text-muted-foreground">
                  {t('adminScreens.brands.columns.carried')}
                </th>
                <th className="h-10 px-3 text-center align-middle font-medium text-muted-foreground">
                  {t('adminScreens.brands.columns.rankedFirst')}
                </th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {brands === null && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {t('adminScreens.brands.loading')}
                  </td>
                </tr>
              )}
              {brands !== null && brands.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    <Tag className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    {q.trim() || filter !== 'ALL'
                      ? t('adminScreens.brands.noMatch')
                      : t('adminScreens.brands.emptyRegistry')}
                  </td>
                </tr>
              )}
              {(brands ?? []).map((b) => (
                <tr key={b.id} className="border-t transition-colors hover:bg-muted/40">
                  {canWrite && (
                    <td className="px-3 py-2 align-middle">
                      <input
                        type="checkbox"
                        checked={selected.has(b.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(b.id);
                          else next.delete(b.id);
                          setSelected(next);
                        }}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 align-middle font-medium">{b.name}</td>
                  <td className="px-3 py-2 text-right align-middle font-mono text-xs tabular-nums text-muted-foreground">
                    {b.tecdoc_sup_id ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    {b.carried ? (
                      <span
                        className="rounded bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success"
                        title={t('adminScreens.brands.carriedTitle')}
                      >
                        {t('adminScreens.brands.carriedBadge')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    <button
                      type="button"
                      disabled={!canWrite || busy}
                      onClick={() => toggleOne(b)}
                      title={
                        b.active
                          ? t('adminScreens.brands.rankedTitle')
                          : t('adminScreens.brands.notRankedTitle')
                      }
                      className={`inline-flex h-5 w-5 items-center justify-center rounded border ${
                        b.active
                          ? 'border-success bg-success/15 text-success'
                          : 'border-input text-transparent hover:border-muted-foreground'
                      } ${canWrite ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
